import type {
  ConstructorGrid,
  GridCell,
  GridPlacement,
  Conflict,
  ValidPosition,
  PlacementOption,
  FittingPair,
  FittingSingleWord,
  Word,
  Pairing,
  AutoSuggestion,
  PairingStatus,
  ListSide
} from '../types'
import { generateId, getLetters, getLetterArray, getLettersFromText } from './pairings'

// Create an empty grid
export function createGrid(width: number, height: number): ConstructorGrid {
  const cells: GridCell[][] = []
  for (let row = 0; row < height; row++) {
    const rowCells: GridCell[] = []
    cells[row] = rowCells
    for (let col = 0; col < width; col++) {
      rowCells[col] = {
        letterA: null,
        letterB: null,
        placementId: null,
        isBlocked: false
      }
    }
  }
  return { width, height, cells, placements: [] }
}

// Check if a placement fits within grid bounds
function placementFitsInGrid(
  grid: ConstructorGrid,
  row: number,
  col: number,
  wordLength: number,
  direction: 'across' | 'down'
): boolean {
  if (row < 0 || col < 0) return false
  if (direction === 'across') {
    return col + wordLength <= grid.width && row < grid.height
  } else {
    return row + wordLength <= grid.height && col < grid.width
  }
}

// Get the cells that a placement would occupy
function getPlacementCells(
  row: number,
  col: number,
  wordLength: number,
  direction: 'across' | 'down'
): Array<{ row: number; col: number }> {
  const cells: Array<{ row: number; col: number }> = []
  for (let i = 0; i < wordLength; i++) {
    if (direction === 'across') {
      cells.push({ row, col: col + i })
    } else {
      cells.push({ row: row + i, col })
    }
  }
  return cells
}

export type PlacePairResult =
  | { success: true; grid: ConstructorGrid; conflicts: Conflict[]; placementId: string }
  | { success: false; error: string }

export type PlacePairInput = Omit<GridPlacement, 'id'> & { id?: string }

// Place a pair on the grid (allows placement anywhere, including outside grid bounds)
export function placePair(
  grid: ConstructorGrid,
  placement: PlacePairInput
): PlacePairResult {
  const wordALetters = getLetterArray(placement.wordAText)
  const wordBLetters = getLetterArray(placement.wordBText)
  const wordLength = wordALetters.length

  // Validate same length
  if (wordALetters.length !== wordBLetters.length) {
    return { success: false, error: 'Words must be same length' }
  }

  const cellPositions = getPlacementCells(placement.row, placement.col, wordLength, placement.direction)

  // Check for blocked cells only within grid bounds
  for (const pos of cellPositions) {
    if (pos.row >= 0 && pos.row < grid.height && pos.col >= 0 && pos.col < grid.width) {
      if (grid.cells[pos.row]?.[pos.col]?.isBlocked) {
        return { success: false, error: 'Cannot place over blocked cell' }
      }
    }
  }

  // Create new placement with ID
  const newPlacement: GridPlacement = {
    ...placement,
    id: placement.id ?? generateId()
  }

  // Clone the grid
  const newGrid: ConstructorGrid = {
    ...grid,
    cells: grid.cells.map(row => row.map(cell => ({ ...cell }))),
    placements: [...grid.placements, newPlacement]
  }

  // Place letters on the grid (only for cells within bounds)
  for (let i = 0; i < wordLength; i++) {
    const pos = cellPositions[i]
    if (!pos) continue
    const letterA = wordALetters[i]
    const letterB = wordBLetters[i]
    // Only update cells that are within grid bounds
    if (pos.row >= 0 && pos.row < grid.height && pos.col >= 0 && pos.col < grid.width) {
      const cell = newGrid.cells[pos.row]?.[pos.col]
      if (!cell || letterA === undefined || letterB === undefined) continue
      cell.letterA = letterA
      cell.letterB = letterB
      cell.placementId = newPlacement.id
    }
  }

  // Find any conflicts
  const conflicts = findConflicts(newGrid)

  return { success: true, grid: newGrid, conflicts, placementId: newPlacement.id }
}

export type PlacementBatchOptions = {
  // Require each placement to intersect (or coincide with) an existing letter
  // when the working grid is non-empty. Off by default — fragments may
  // legitimately add disconnected groups.
  requireIntersection?: boolean
  // Treat '□' as wildcards on either the cell or the candidate side. Default true.
  allowWildcard?: boolean
  // Strip these placement ids from the grid first, so the batch is checked as
  // if they weren't there. Used when validating a "move" — own-placement masking.
  ignorePlacementIds?: readonly string[]
}

export type PlacementBatchReason =
  | 'wrong-length'
  | 'out-of-bounds'
  | 'blocked-cell'
  | 'letter-conflict'
  | 'adjacency-parallel-run'
  | 'no-intersection'

export type PlacementBatchResult =
  | { valid: true; grid: ConstructorGrid; placementIds: string[] }
  | { valid: false; reasons: Array<{ index: number; reason: PlacementBatchReason; detail?: string }> }

/**
 * Apply a batch of placements to a grid, validating each step against the
 * SAME constraints `findPlacementOptions` enforces: bounds, blocked cells,
 * letter conflicts (with '□' wildcard), adjacency / parallel-run, and an
 * optional intersection requirement. Each placement is applied to the
 * running grid, so a later placement can satisfy intersection / adjacency
 * via an earlier one in the same batch.
 *
 * If any step fails, returns { valid: false } listing every failure (the
 * batch is reported as a whole — earlier successes aren't applied to the
 * returned grid).
 */
export function validatePlacementBatch(
  grid: ConstructorGrid,
  placements: PlacePairInput[],
  options: PlacementBatchOptions = {}
): PlacementBatchResult {
  const { requireIntersection = false, allowWildcard = true, ignorePlacementIds = [] } = options
  const reasons: Array<{ index: number; reason: PlacementBatchReason; detail?: string }> = []

  // Strip ignored placements first so the batch is checked against a grid
  // where they don't exist (caller is, e.g., moving them).
  let working: ConstructorGrid = grid
  if (ignorePlacementIds.length > 0) {
    for (const id of ignorePlacementIds) {
      working = removePlacement(working, id)
    }
  }

  const placementIds: string[] = []

  for (let i = 0; i < placements.length; i++) {
    const p = placements[i]!
    const wordALetters = getLetterArray(p.wordAText).join('')
    const wordBLetters = getLetterArray(p.wordBText).join('')

    const evalResult = evaluatePlacementCandidate(
      working,
      p.row,
      p.col,
      p.direction,
      wordALetters,
      wordBLetters,
      { allowWildcard },
    )

    if (!evalResult.fits) {
      const reason: PlacementBatchReason = evalResult.reason === 'wrong-length' ? 'wrong-length'
        : evalResult.reason === 'out-of-bounds' ? 'out-of-bounds'
        : evalResult.reason === 'blocked-cell' ? 'blocked-cell'
        : evalResult.reason === 'adjacency-parallel-run' ? 'adjacency-parallel-run'
        : 'letter-conflict'
      const entry: { index: number; reason: PlacementBatchReason; detail?: string } = { index: i, reason }
      if (evalResult.detail !== undefined) entry.detail = evalResult.detail
      reasons.push(entry)
      continue
    }

    if (requireIntersection && working.placements.length > 0 && evalResult.rawIntersectionCount === 0) {
      reasons.push({ index: i, reason: 'no-intersection' })
      continue
    }

    // Apply this placement and continue with the new grid.
    const result = placePair(working, p)
    if (!result.success) {
      reasons.push({ index: i, reason: 'letter-conflict', detail: result.error })
      continue
    }
    working = result.grid
    placementIds.push(result.placementId)
  }

  if (reasons.length > 0) return { valid: false, reasons }
  return { valid: true, grid: working, placementIds }
}

// Remove a placement from the grid
export function removePlacement(
  grid: ConstructorGrid,
  placementId: string
): ConstructorGrid {
  const placement = grid.placements.find(p => p.id === placementId)
  if (!placement) return grid

  const wordLength = getLettersFromText(placement.wordAText).length
  const cellPositions = getPlacementCells(placement.row, placement.col, wordLength, placement.direction)

  // Clone the grid
  const newGrid: ConstructorGrid = {
    ...grid,
    cells: grid.cells.map(row => row.map(cell => ({ ...cell }))),
    placements: grid.placements.filter(p => p.id !== placementId)
  }

  // Clear the cells (but check if other placements use them)
  // Only process cells that are within grid bounds
  for (const pos of cellPositions) {
    // Skip cells outside grid bounds
    if (pos.row < 0 || pos.row >= grid.height || pos.col < 0 || pos.col >= grid.width) {
      continue
    }

    const cell = newGrid.cells[pos.row]?.[pos.col]
    if (!cell) continue
    if (cell.placementId === placementId) {
      // Check if any other placement uses this cell
      const otherPlacement = newGrid.placements.find(p => {
        const otherLength = getLettersFromText(p.wordAText).length
        const otherCells = getPlacementCells(p.row, p.col, otherLength, p.direction)
        return otherCells.some(c => c.row === pos.row && c.col === pos.col)
      })

      if (otherPlacement) {
        // Get the letter from the other placement
        const otherALetters = getLetterArray(otherPlacement.wordAText)
        const otherBLetters = getLetterArray(otherPlacement.wordBText)
        const otherCells = getPlacementCells(otherPlacement.row, otherPlacement.col, otherALetters.length, otherPlacement.direction)
        const idx = otherCells.findIndex(c => c.row === pos.row && c.col === pos.col)
        const letterA = otherALetters[idx]
        const letterB = otherBLetters[idx]
        cell.letterA = letterA ?? null
        cell.letterB = letterB ?? null
        cell.placementId = otherPlacement.id
      } else {
        cell.letterA = null
        cell.letterB = null
        cell.placementId = null
      }
    }
  }

  return newGrid
}

// Find all conflicts in the current grid
export function findConflicts(grid: ConstructorGrid): Conflict[] {
  const conflicts: Conflict[] = []
  const cellLetters: Map<string, Array<{
    placementId: string
    letterA: string
    letterB: string
  }>> = new Map()

  // Build a map of all letters at each cell position
  for (const placement of grid.placements) {
    const wordALetters = getLetterArray(placement.wordAText)
    const wordBLetters = getLetterArray(placement.wordBText)
    const cellPositions = getPlacementCells(placement.row, placement.col, wordALetters.length, placement.direction)

    for (let i = 0; i < cellPositions.length; i++) {
      const pos = cellPositions[i]
      if (!pos) continue
      const key = `${pos.row},${pos.col}`
      const letterA = wordALetters[i]
      const letterB = wordBLetters[i]
      if (letterA === undefined || letterB === undefined) continue

      if (!cellLetters.has(key)) {
        cellLetters.set(key, [])
      }

      cellLetters.get(key)!.push({
        placementId: placement.id,
        letterA,
        letterB
      })
    }
  }

  // Find cells with conflicting letters
  for (const [key, entries] of cellLetters) {
    if (entries.length > 1) {
      const parts = key.split(',')
      const row = Number(parts[0])
      const col = Number(parts[1])
      const firstEntry = entries[0]
      const lastEntry = entries[entries.length - 1]
      if (!firstEntry || !lastEntry) continue

      // Check for conflicts in A letters
      const uniqueA = new Set(entries.map(e => e.letterA))
      const uniqueB = new Set(entries.map(e => e.letterB))

      if (uniqueA.size > 1 || uniqueB.size > 1) {
        conflicts.push({
          row,
          col,
          letterA: {
            expected: firstEntry.letterA,
            found: lastEntry.letterA
          },
          letterB: {
            expected: firstEntry.letterB,
            found: lastEntry.letterB
          },
          placementIds: entries.map(e => e.placementId)
        })
      }
    }
  }

  return conflicts
}

// Find valid positions for a given pair
export function findValidPositions(
  grid: ConstructorGrid,
  wordA: string,
  wordB: string
): ValidPosition[] {
  const wordALetters = getLettersFromText(wordA)
  const wordBLetters = getLettersFromText(wordB)
  const wordLength = wordALetters.length

  if (wordALetters.length !== wordBLetters.length) {
    return []
  }

  const validPositions: ValidPosition[] = []

  // Try every position and direction. evaluatePlacementCandidate centralises
  // bounds / blocked / letter-conflict / adjacency rules; we skip positions
  // that fail bounds or blocked outright (they aren't "valid" in any sense),
  // but still surface positions with letter conflicts or adjacency issues so
  // existing callers can inspect the conflict list.
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      for (const direction of ['across', 'down'] as const) {
        if (!placementFitsInGrid(grid, row, col, wordLength, direction)) {
          continue
        }
        const evalResult = evaluatePlacementCandidate(grid, row, col, direction, wordALetters, wordBLetters)
        if (evalResult.reason === 'blocked-cell') continue
        validPositions.push({ row, col, direction, conflicts: evalResult.conflicts })
      }
    }
  }

  return validPositions
}

// Find every conflict-free spot a specific pair can go, ranked by how many
// OTHER existing placements it would cross. The pair's own existing
// placements (matched by pairingId or by identical letters) are excluded:
// their positions are skipped, and cells they exclusively occupy don't
// count as intersections (the pair shouldn't "intersect with itself").
export function findPlacementOptions(
  grid: ConstructorGrid,
  wordAText: string,
  wordBText: string,
  options: { requireIntersection?: boolean; maxResults?: number; pairingId?: string | null } = {}
): PlacementOption[] {
  const { requireIntersection = grid.placements.length > 0, maxResults = 50, pairingId = null } = options
  const wordALetters = getLettersFromText(wordAText)
  const wordBLetters = getLettersFromText(wordBText)
  const wordLength = wordALetters.length

  // Identify the pair's own placements on the grid.
  const ownershipMaps = buildCellOwnershipMaps(grid)
  const ownIds = new Set<string>()
  if (pairingId) {
    for (const id of ownershipMaps.placementsByPairingId.get(pairingId) ?? []) {
      ownIds.add(id)
    }
  }
  for (const id of ownershipMaps.placementsByLetterCombo.get(`${wordALetters}::${wordBLetters}`) ?? []) {
    ownIds.add(id)
  }
  const ownCellKeys = computeOwnCellKeys(ownIds, ownershipMaps)
  const ownPositionKeys = new Set<string>()
  for (const p of grid.placements) {
    if (ownIds.has(p.id)) ownPositionKeys.add(`${p.row},${p.col},${p.direction}`)
  }
  const otherPlacementCount = grid.placements.length - ownIds.size

  // Iterate every (row, col, direction) and let evaluatePlacementCandidate
  // enforce all constraints (bounds, blocked, conflict, adjacency, wildcards,
  // ownCellKeys masking). countIntersections is still the source for the
  // ranking metric — the evaluator's intersection counts are masked by
  // ownCellKeys, which is what we want here.
  const options_: PlacementOption[] = []
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      for (const direction of ['across', 'down'] as const) {
        if (ownPositionKeys.has(`${row},${col},${direction}`)) continue
        const evalResult = evaluatePlacementCandidate(
          grid,
          row,
          col,
          direction,
          wordALetters,
          wordBLetters,
          { ownCellKeys },
        )
        if (!evalResult.fits) continue
        const intersectionCount = countIntersections(grid, row, col, wordLength, direction, ownCellKeys)
        if (requireIntersection && otherPlacementCount > 0 && intersectionCount === 0) continue
        options_.push({ row, col, direction, intersectionCount })
      }
    }
  }

  // More intersections = more constrained = better. Tiebreak by row then col
  // for stable ordering.
  options_.sort((a, b) => {
    if (a.intersectionCount !== b.intersectionCount) return b.intersectionCount - a.intersectionCount
    if (a.row !== b.row) return a.row - b.row
    return a.col - b.col
  })

  return options_.slice(0, maxResults)
}

// Get words already used in grid placements, including variant-group words.
export function getUsedWordIds(grid: ConstructorGrid, pairings: Pairing[], words: Word[]): Set<string> {
  const usedWordIds = new Set<string>()

  const pairingsById = new Map(pairings.map(pairing => [pairing.id, pairing]))
  const wordsByListAndLetters = new Map<string, Word>()
  const wordsById = new Map(words.map(word => [word.id, word]))
  const groupMembersByGroupId = new Map<string, string[]>()
  const twinMembersByTwinGroupId = new Map<string, string[]>()

  for (const word of words) {
    if (word.list) {
      wordsByListAndLetters.set(`${word.list}:${getLetters(word)}`, word)
    }
    if (word.groupId) {
      const members = groupMembersByGroupId.get(word.groupId)
      if (members) members.push(word.id)
      else groupMembersByGroupId.set(word.groupId, [word.id])
    }
    if (word.twinGroupId) {
      const members = twinMembersByTwinGroupId.get(word.twinGroupId)
      if (members) members.push(word.id)
      else twinMembersByTwinGroupId.set(word.twinGroupId, [word.id])
    }
  }

  for (const placement of grid.placements) {
    if (placement.pairingId) {
      const pairing = pairingsById.get(placement.pairingId)
      if (pairing) {
        usedWordIds.add(pairing.wordAId)
        usedWordIds.add(pairing.wordBId)
      }
    }

    const placedA = getLettersFromText(placement.wordAText)
    const placedB = getLettersFromText(placement.wordBText)

    if (placedA && !placement.wordAText.includes('□')) {
      const wordA = wordsByListAndLetters.get(`A:${placedA}`)
      if (wordA) usedWordIds.add(wordA.id)
    }
    if (placedB && !placement.wordBText.includes('□')) {
      const wordB = wordsByListAndLetters.get(`B:${placedB}`)
      if (wordB) usedWordIds.add(wordB.id)
    }
  }

  const expandedUsedWordIds = new Set(usedWordIds)
  for (const usedWordId of usedWordIds) {
    const usedWord = wordsById.get(usedWordId)
    if (!usedWord) continue
    if (usedWord.groupId) {
      for (const groupMemberId of groupMembersByGroupId.get(usedWord.groupId) ?? []) {
        expandedUsedWordIds.add(groupMemberId)
      }
    }
    if (usedWord.twinGroupId) {
      for (const twinMemberId of twinMembersByTwinGroupId.get(usedWord.twinGroupId) ?? []) {
        expandedUsedWordIds.add(twinMemberId)
      }
    }
  }

  return expandedUsedWordIds
}

// Get pairing IDs already placed on grid
function getUsedPairingIds(grid: ConstructorGrid): Set<string> {
  const usedPairingIds = new Set<string>()
  for (const placement of grid.placements) {
    if (placement.pairingId) {
      usedPairingIds.add(placement.pairingId)
    }
  }
  return usedPairingIds
}

// Check if placing would create invalid adjacent letters (parallel runs)
// Returns true if the placement would create problems.
// `ownCellKeys` (when provided) names cells whose letters should be ignored —
// used by includePlaced to evaluate a placement at other positions without
// being blocked by its own current footprint.
function hasAdjacentConflicts(
  grid: ConstructorGrid,
  row: number,
  col: number,
  wordLength: number,
  direction: 'across' | 'down',
  ownCellKeys?: Set<string>
): boolean {
  const cellHasLetter = (r: number, c: number): boolean => {
    if (r < 0 || r >= grid.height || c < 0 || c >= grid.width) return false
    if (ownCellKeys?.has(`${r},${c}`)) return false
    const cell = grid.cells[r]?.[c]
    return cell !== undefined && (cell.letterA !== null || cell.letterB !== null)
  }

  const cellPositions = getPlacementCells(row, col, wordLength, direction)

  for (let i = 0; i < cellPositions.length; i++) {
    const pos = cellPositions[i]
    if (!pos) continue

    // Only check empty cells - if cell already has letters, it's an intersection (handled elsewhere)
    if (cellHasLetter(pos.row, pos.col)) continue

    // Check perpendicular adjacent cells
    const adjacentOffsets = direction === 'across'
      ? [{ dr: -1, dc: 0 }, { dr: 1, dc: 0 }]  // above and below for horizontal word
      : [{ dr: 0, dc: -1 }, { dr: 0, dc: 1 }]  // left and right for vertical word

    for (const offset of adjacentOffsets) {
      if (cellHasLetter(pos.row + offset.dr, pos.col + offset.dc)) return true
    }
  }

  // Also check cells immediately before and after the word
  // (words shouldn't directly extend existing words)
  const beforeRow = direction === 'down' ? row - 1 : row
  const beforeCol = direction === 'across' ? col - 1 : col
  if (cellHasLetter(beforeRow, beforeCol)) return true

  const afterRow = direction === 'down' ? row + wordLength : row
  const afterCol = direction === 'across' ? col + wordLength : col
  if (cellHasLetter(afterRow, afterCol)) return true

  return false
}

// --- Single shared placement evaluator ---
//
// Every "does this placement fit?" check in the codebase used to have its own
// inlined loop with subtle differences (one missed adjacency, one missed
// wildcards, etc.). This is the single source of truth: it iterates a
// candidate's cells and reports whether the placement satisfies all the rules
// the constructor cares about — bounds, blocked cells, letter conflict (with
// '□' wildcard handling), adjacency / parallel-run, plus optional ownership
// masking and per-cell relaxations.

export type EvaluatePlacementOptions = {
  // Cells whose existing letters should be ignored — used by includePlaced
  // searches so a candidate isn't blocked by its own current footprint.
  ownCellKeys?: Set<string> | undefined
  // Treat '□' on either the cell or the candidate as a wildcard. Default true.
  allowWildcard?: boolean | undefined
  // Allow cells outside the grid (only for non-active cells, when
  // activeCellIndex is provided). Default false.
  allowOutOfBounds?: boolean | undefined
  // Allow letter conflicts away from the candidate's "active" cell — used by
  // cell-mode suggestion searches that anchor a candidate at a specific cell.
  // Has no effect unless activeCellIndex is set.
  allowConflictsAwayFromActive?: boolean | undefined
  // Letter index that's the candidate's "active" cell (the one anchored to
  // the user's selected grid cell). Drives allowOutOfBounds /
  // allowConflictsAwayFromActive.
  activeCellIndex?: number | undefined
  // Skip the adjacency check. Rare — fragments / drag-mover paths that don't
  // care about the parallel-run rule set this. Default false.
  skipAdjacencyCheck?: boolean | undefined
}

export type EvaluatePlacementReason =
  | 'wrong-length'
  | 'out-of-bounds'
  | 'blocked-cell'
  | 'letter-conflict'
  | 'adjacency-parallel-run'

export type EvaluatePlacementResult = {
  fits: boolean
  // Cells in the candidate's range where any side matches existing AND no
  // side conflicts. Half-intersections (match A, conflict B) are excluded.
  validIntersectionCount: number
  // Cells with an existing letter on at least one side (after masking and
  // wildcard handling), regardless of match/conflict.
  rawIntersectionCount: number
  conflicts: Conflict[]
  reason?: EvaluatePlacementReason | undefined
  detail?: string | undefined
}

export function evaluatePlacementCandidate(
  grid: ConstructorGrid,
  row: number,
  col: number,
  direction: 'across' | 'down',
  wordALetters: string,
  wordBLetters: string,
  options: EvaluatePlacementOptions = {},
): EvaluatePlacementResult {
  const {
    ownCellKeys,
    allowWildcard = true,
    allowOutOfBounds = false,
    allowConflictsAwayFromActive = false,
    activeCellIndex,
    skipAdjacencyCheck = false,
  } = options

  const wordLength = wordALetters.length
  if (wordLength === 0 || wordLength !== wordBLetters.length) {
    return { fits: false, validIntersectionCount: 0, rawIntersectionCount: 0, conflicts: [], reason: 'wrong-length' }
  }

  if (!allowOutOfBounds && !placementFitsInGrid(grid, row, col, wordLength, direction)) {
    return { fits: false, validIntersectionCount: 0, rawIntersectionCount: 0, conflicts: [], reason: 'out-of-bounds' }
  }

  const cellPositions = getPlacementCells(row, col, wordLength, direction)
  const conflicts: Conflict[] = []
  let validIntersectionCount = 0
  let rawIntersectionCount = 0
  let fits = true
  let reason: EvaluatePlacementReason | undefined
  let detail: string | undefined

  for (let i = 0; i < cellPositions.length; i++) {
    const pos = cellPositions[i]!
    const isActiveCell = activeCellIndex !== undefined && i === activeCellIndex
    const inBounds = pos.row >= 0 && pos.row < grid.height && pos.col >= 0 && pos.col < grid.width

    if (!inBounds) {
      if (allowOutOfBounds && !isActiveCell) continue
      fits = false
      reason = 'out-of-bounds'
      break
    }

    const cell = grid.cells[pos.row]?.[pos.col]
    if (!cell) {
      if (allowOutOfBounds && !isActiveCell) continue
      fits = false
      reason = 'out-of-bounds'
      break
    }

    if (cell.isBlocked) {
      fits = false
      reason = 'blocked-cell'
      break
    }

    const expectedA = wordALetters[i]!
    const expectedB = wordBLetters[i]!

    const cellOwned = ownCellKeys?.has(`${pos.row},${pos.col}`) ?? false
    const effLetterA = cellOwned ? null : cell.letterA
    const effLetterB = cellOwned ? null : cell.letterB

    const cellAReal = effLetterA !== null && (!allowWildcard || effLetterA !== '□')
    const cellBReal = effLetterB !== null && (!allowWildcard || effLetterB !== '□')
    const candAReal = !allowWildcard || expectedA !== '□'
    const candBReal = !allowWildcard || expectedB !== '□'

    const matchesA = cellAReal && candAReal && effLetterA === expectedA
    const matchesB = cellBReal && candBReal && effLetterB === expectedB
    const conflictA = cellAReal && candAReal && effLetterA !== expectedA
    const conflictB = cellBReal && candBReal && effLetterB !== expectedB

    if (cellAReal || cellBReal) rawIntersectionCount++
    if ((matchesA || matchesB) && !conflictA && !conflictB) validIntersectionCount++

    if (conflictA || conflictB) {
      conflicts.push({
        row: pos.row,
        col: pos.col,
        letterA: { expected: expectedA, found: cell.letterA ?? '' },
        letterB: { expected: expectedB, found: cell.letterB ?? '' },
        placementIds: cell.placementId ? [cell.placementId] : [],
      })
      if (allowConflictsAwayFromActive && !isActiveCell) continue
      if (fits) {
        fits = false
        reason = 'letter-conflict'
        detail = `at (${pos.row},${pos.col}): ${cell.letterA ?? '_'}/${cell.letterB ?? '_'} vs ${expectedA}/${expectedB}`
      }
    }
  }

  if (fits && !skipAdjacencyCheck) {
    if (hasAdjacentConflicts(grid, row, col, wordLength, direction, ownCellKeys)) {
      fits = false
      reason = 'adjacency-parallel-run'
    }
  }

  const result: EvaluatePlacementResult = { fits, validIntersectionCount, rawIntersectionCount, conflicts }
  if (reason !== undefined) result.reason = reason
  if (detail !== undefined) result.detail = detail
  return result
}

// Count number of intersections a placement would create.
// `ownCellKeys` (when provided) marks cells whose letters belong to the
// candidate's own existing placements and should not count as intersections.
function countIntersections(
  grid: ConstructorGrid,
  row: number,
  col: number,
  wordLength: number,
  direction: 'across' | 'down',
  ownCellKeys?: Set<string>
): number {
  const cellPositions = getPlacementCells(row, col, wordLength, direction)
  let count = 0

  for (const pos of cellPositions) {
    if (pos.row >= 0 && pos.row < grid.height && pos.col >= 0 && pos.col < grid.width) {
      if (ownCellKeys?.has(`${pos.row},${pos.col}`)) continue
      const cell = grid.cells[pos.row]?.[pos.col]
      if (cell && (cell.letterA !== null || cell.letterB !== null)) {
        count++
      }
    }
  }
  return count
}

type WordMatchForm = {
  sourceWord: Word
  text: string
  letters: string
  isBase: boolean
}

export type PairCandidate = {
  pairingId: string | null
  sourcePairingId: string | null
  wordA: Word
  wordB: Word
  wordAText: string
  wordBText: string
  wordALetters: string
  wordBLetters: string
}

export type InflectionRuleOptions = {
  plurals?: boolean
  pastTense?: boolean
  presentParticiple?: boolean
}

const DEFAULT_INFLECTION_RULES: Required<InflectionRuleOptions> = {
  plurals: true,
  pastTense: true,
  presentParticiple: true
}

function isVowel(letter: string): boolean {
  return /[AEIOU]/.test(letter)
}

function isConsonant(letter: string): boolean {
  return /[B-DF-HJ-NP-TV-Z]/.test(letter)
}

function isAsciiAlphaWord(letters: string): boolean {
  return /^[A-Z]+$/.test(letters)
}

function shouldDoubleFinalConsonant(letters: string): boolean {
  if (letters.length < 3 || !isAsciiAlphaWord(letters)) return false
  const first = letters[letters.length - 3]
  const second = letters[letters.length - 2]
  const third = letters[letters.length - 1]
  if (!first || !second || !third) return false
  if (!isConsonant(first) || !isVowel(second) || !isConsonant(third)) return false
  if (third === 'W' || third === 'X' || third === 'Y') return false
  return true
}

function isLikelyAlreadyInflected(letters: string): boolean {
  if (!isAsciiAlphaWord(letters)) return false
  if (letters.length > 4 && (letters.endsWith('ING') || letters.endsWith('IED') || letters.endsWith('ED'))) {
    return true
  }
  if (letters.length > 3 && letters.endsWith('IES')) return true
  if (letters.length > 3 && /(?:SES|XES|ZES|CHES|SHES|OES)$/.test(letters)) return true
  if (letters.length > 3 && letters.endsWith('S') && !letters.endsWith('SS')) return true
  return false
}

function getInflectedLetterForms(
  baseLetters: string,
  inflectionRules: Required<InflectionRuleOptions>
): string[] {
  const forms = new Set<string>([baseLetters])
  if (!isAsciiAlphaWord(baseLetters)) return Array.from(forms)
  if (isLikelyAlreadyInflected(baseLetters)) return Array.from(forms)

  const last = baseLetters[baseLetters.length - 1]
  const beforeLast = baseLetters[baseLetters.length - 2]

  if (inflectionRules.plurals) {
    // Plurals / third-person singular
    forms.add(`${baseLetters}S`)
    if (/(?:S|X|Z|CH|SH|O)$/.test(baseLetters)) {
      forms.add(`${baseLetters}ES`)
    }
    if (last === 'Y' && beforeLast && isConsonant(beforeLast)) {
      forms.add(`${baseLetters.slice(0, -1)}IES`)
    }
  }

  if (inflectionRules.pastTense) {
    // Past tense
    if (last === 'Y' && beforeLast && isConsonant(beforeLast)) {
      forms.add(`${baseLetters.slice(0, -1)}IED`)
    }
    if (last === 'E') {
      forms.add(`${baseLetters}D`)
    } else {
      forms.add(`${baseLetters}ED`)
    }
    if (shouldDoubleFinalConsonant(baseLetters) && last) {
      forms.add(`${baseLetters}${last}ED`)
    }
  }

  if (inflectionRules.presentParticiple) {
    // Present participle
    if (baseLetters.endsWith('IE')) {
      forms.add(`${baseLetters.slice(0, -2)}YING`)
    }
    if (last === 'E' && !baseLetters.endsWith('EE')) {
      forms.add(`${baseLetters.slice(0, -1)}ING`)
    } else {
      forms.add(`${baseLetters}ING`)
    }
    if (shouldDoubleFinalConsonant(baseLetters) && last) {
      forms.add(`${baseLetters}${last}ING`)
    }
  }

  return Array.from(forms)
}

function getWordMatchForms(
  word: Word,
  includeInflections: boolean = true,
  inflectionRules: Required<InflectionRuleOptions> = DEFAULT_INFLECTION_RULES
): WordMatchForm[] {
  const baseLetters = getLetters(word)
  const formsByLetters = new Map<string, WordMatchForm>()

  formsByLetters.set(baseLetters, {
    sourceWord: word,
    text: word.text,
    letters: baseLetters,
    isBase: true
  })

  if (includeInflections) {
    for (const inflectedLetters of getInflectedLetterForms(baseLetters, inflectionRules)) {
      if (inflectedLetters === baseLetters) continue
      formsByLetters.set(inflectedLetters, {
        sourceWord: word,
        text: inflectedLetters,
        letters: inflectedLetters,
        isBase: false
      })
    }
  }

  return Array.from(formsByLetters.values())
}

type GeneratePairsOptions = {
  existingOnly?: boolean
  maxLength?: number
  includeInflections?: boolean
  inflectionRules?: InflectionRuleOptions
}

export function suggestInflectedForms(
  wordText: string,
  options: { inflectionRules?: InflectionRuleOptions } = {}
): string[] {
  const { inflectionRules: rawInflectionRules } = options
  const inflectionRules = { ...DEFAULT_INFLECTION_RULES, ...rawInflectionRules }
  const baseLetters = getLettersFromText(wordText)
  return getInflectedLetterForms(baseLetters, inflectionRules).filter(form => form !== baseLetters)
}

// Generate all valid pair combinations from words (including inflected forms).
export function generateAllValidPairs(
  words: Word[],
  existingPairings: Pairing[],
  options: GeneratePairsOptions = {}
): PairCandidate[] {
  const {
    existingOnly = false,
    maxLength,
    includeInflections = true,
    inflectionRules: rawInflectionRules
  } = options
  const inflectionRules = { ...DEFAULT_INFLECTION_RULES, ...rawInflectionRules }
  const listAWords = words.filter(w => w.list === 'A')
  const listBWords = words.filter(w => w.list === 'B')

  // Create a map for quick lookup of existing pairings
  const existingPairingMap = new Map<string, string>()
  for (const p of existingPairings) {
    existingPairingMap.set(`${p.wordAId}:${p.wordBId}`, p.id)
  }

  // Bucket B-side forms by length so we only compare equal-length forms.
  const formsBByLength = new Map<number, WordMatchForm[]>()
  for (const wordB of listBWords) {
    for (const formB of getWordMatchForms(wordB, includeInflections, inflectionRules)) {
      if (maxLength !== undefined && formB.letters.length > maxLength) continue
      const bucket = formsBByLength.get(formB.letters.length)
      if (bucket) bucket.push(formB)
      else formsBByLength.set(formB.letters.length, [formB])
    }
  }

  const seenPairs = new Set<string>()
  const pairs: PairCandidate[] = []

  for (const wordA of listAWords) {
    for (const formA of getWordMatchForms(wordA, includeInflections, inflectionRules)) {
      const wordLength = formA.letters.length
      if (maxLength !== undefined && wordLength > maxLength) continue
      const matchingBForms = formsBByLength.get(wordLength)
      if (!matchingBForms) continue

      for (const formB of matchingBForms) {
        const sourcePairingId = existingPairingMap.get(`${wordA.id}:${formB.sourceWord.id}`) ?? null
        if (existingOnly && !sourcePairingId) continue

        // Only use a real pairing ID when both forms are the base words.
        // Inflected forms are always virtual placements.
        const pairingId = sourcePairingId && formA.isBase && formB.isBase ? sourcePairingId : null
        const key = `${wordA.id}:${formB.sourceWord.id}:${formA.letters}:${formB.letters}:${pairingId ?? 'virtual'}`
        if (seenPairs.has(key)) continue
        seenPairs.add(key)

        pairs.push({
          pairingId,
          sourcePairingId,
          wordA,
          wordB: formB.sourceWord,
          wordAText: formA.text,
          wordBText: formB.text,
          wordALetters: formA.letters,
          wordBLetters: formB.letters
        })
      }
    }
  }

  return pairs
}

// Indexes used by `includePlaced` to figure out which existing placements
// represent a given candidate, and which cells those placements occupy.
type CellOwnershipMaps = {
  cellToPlacementIds: Map<string, string[]>
  placementToCellKeys: Map<string, string[]>
  placementsByPairingId: Map<string, string[]>
  placementsByLetterCombo: Map<string, string[]>
  placementsByWordOnList: Map<string, string[]>
}

function buildCellOwnershipMaps(grid: ConstructorGrid): CellOwnershipMaps {
  const cellToPlacementIds = new Map<string, string[]>()
  const placementToCellKeys = new Map<string, string[]>()
  const placementsByPairingId = new Map<string, string[]>()
  const placementsByLetterCombo = new Map<string, string[]>()
  const placementsByWordOnList = new Map<string, string[]>()

  const pushOrInit = (map: Map<string, string[]>, key: string, value: string) => {
    const list = map.get(key)
    if (list) list.push(value)
    else map.set(key, [value])
  }

  for (const p of grid.placements) {
    const aLetters = getLettersFromText(p.wordAText)
    const bLetters = getLettersFromText(p.wordBText)
    const cells = getPlacementCells(p.row, p.col, aLetters.length, p.direction)
    const cellKeys: string[] = []
    for (const pos of cells) {
      const key = `${pos.row},${pos.col}`
      cellKeys.push(key)
      pushOrInit(cellToPlacementIds, key, p.id)
    }
    placementToCellKeys.set(p.id, cellKeys)

    if (p.pairingId) pushOrInit(placementsByPairingId, p.pairingId, p.id)
    pushOrInit(placementsByLetterCombo, `${aLetters}::${bLetters}`, p.id)
    pushOrInit(placementsByWordOnList, `A:${aLetters}`, p.id)
    pushOrInit(placementsByWordOnList, `B:${bLetters}`, p.id)
  }

  return {
    cellToPlacementIds,
    placementToCellKeys,
    placementsByPairingId,
    placementsByLetterCombo,
    placementsByWordOnList,
  }
}

// Cells "owned" exclusively by `ownPlacementIds` should be treated as empty
// when re-evaluating a placed candidate at other positions. A cell shared with
// any other placement stays constraining.
function computeOwnCellKeys(
  ownPlacementIds: Set<string>,
  maps: CellOwnershipMaps
): Set<string> {
  const ownCellKeys = new Set<string>()
  for (const pid of ownPlacementIds) {
    for (const cellKey of maps.placementToCellKeys.get(pid) ?? []) {
      const occupants = maps.cellToPlacementIds.get(cellKey) ?? []
      if (occupants.every(oid => ownPlacementIds.has(oid))) {
        ownCellKeys.add(cellKey)
      }
    }
  }
  return ownCellKeys
}

// Find pairs that fit at a given position with crossing constraints
// The word can pass through the selected cell at any letter position, not just start there
export function findFittingPairs(
  grid: ConstructorGrid,
  row: number,
  col: number,
  direction: 'across' | 'down',
  maxLength: number,
  words: Word[],
  pairings: Pairing[],
  options: {
    requireIntersection?: boolean
    includeAllPairs?: boolean
    includeInflections?: boolean
    inflectionRules?: InflectionRuleOptions
    allowOutOfBounds?: boolean
    allowConflictsAwayFromActive?: boolean
    includePlaced?: boolean
  } = {}
): FittingPair[] {
  const {
    requireIntersection = true,
    includeAllPairs = true,
    includeInflections = true,
    inflectionRules,
    allowOutOfBounds = false,
    allowConflictsAwayFromActive = false,
    includePlaced = false,
  } = options
  const fittingPairs: FittingPair[] = []
  const addedPairs = new Set<string>() // Track unique pair+offset combinations
  const validPositionCountByText = new Map<string, number>()

  // Get words and pairings already used on grid
  const usedPairingIds = getUsedPairingIds(grid)
  const usedWordIds = getUsedWordIds(grid, pairings, words)

  // includePlaced needs to evaluate placed candidates as if their existing
  // footprint weren't there — otherwise the only "fit" found is the cell range
  // they already sit on. Build cell ownership maps once.
  const ownershipMaps = includePlaced ? buildCellOwnershipMaps(grid) : null

  // Generate candidate pairs
  const generatePairOptions: GeneratePairsOptions = {
    existingOnly: !includeAllPairs,
    maxLength,
    includeInflections,
    ...(inflectionRules ? { inflectionRules } : {})
  }
  const candidatePairs = generateAllValidPairs(words, pairings, generatePairOptions)

  // Check each candidate pair
  for (const { pairingId, sourcePairingId, wordA, wordB, wordAText, wordBText, wordALetters, wordBLetters } of candidatePairs) {
    // Skip if this source pairing is already on the grid.
    if (!includePlaced && sourcePairingId && usedPairingIds.has(sourcePairingId)) continue

    // Skip if either word is already used in another placement
    if (!includePlaced && (usedWordIds.has(wordA.id) || usedWordIds.has(wordB.id))) continue

    const wordLength = wordALetters.length

    if (wordLength > maxLength) continue

    // For includePlaced: identify this candidate's own placements so we can
    // mask their cells from the constraint and adjacency checks, and skip
    // the exact existing positions (which would otherwise always re-match).
    let ownCellKeys: Set<string> | undefined
    let ownPlacementCount = 0
    let ownPositionKeys: Set<string> | undefined
    if (ownershipMaps) {
      const ownIds = new Set<string>()
      if (sourcePairingId) {
        for (const id of ownershipMaps.placementsByPairingId.get(sourcePairingId) ?? []) {
          ownIds.add(id)
        }
      }
      for (const id of ownershipMaps.placementsByLetterCombo.get(`${wordALetters}::${wordBLetters}`) ?? []) {
        ownIds.add(id)
      }
      if (ownIds.size > 0) {
        ownCellKeys = computeOwnCellKeys(ownIds, ownershipMaps)
        ownPlacementCount = ownIds.size
        ownPositionKeys = new Set()
        for (const p of grid.placements) {
          if (ownIds.has(p.id)) {
            ownPositionKeys.add(`${p.row},${p.col},${p.direction}`)
          }
        }
      }
    }

    // Try all possible starting positions where the word could pass through (row, col)
    // startOffset is how many cells before the selected cell the word starts
    for (let startOffset = 0; startOffset < wordLength; startOffset++) {
      // Calculate the actual starting position
      const startRow = direction === 'down' ? row - startOffset : row
      const startCol = direction === 'across' ? col - startOffset : col

      // Skip if starting position is out of bounds (negative)
      if (startRow < 0 || startCol < 0) continue

      // Skip the candidate's own existing positions
      if (ownPositionKeys?.has(`${startRow},${startCol},${direction}`)) continue

      // Run the full constraint check via the shared evaluator. It already
      // handles bounds (allowOutOfBounds), blocked cells, letter conflicts
      // (with '□' wildcard, ownCellKeys masking, allowConflictsAwayFromActive),
      // adjacency / parallel-run, and half-intersection counting in one go.
      const evalResult = evaluatePlacementCandidate(
        grid,
        startRow,
        startCol,
        direction,
        wordALetters,
        wordBLetters,
        {
          ownCellKeys,
          allowOutOfBounds,
          allowConflictsAwayFromActive,
          activeCellIndex: startOffset,
        },
      )
      if (!evalResult.fits) continue

      // If grid is empty (or only contains this candidate's own placements),
      // allow placement without intersection. Otherwise require a *valid*
      // intersection (matching existing letter).
      const gridHasOtherPlacements = grid.placements.length - ownPlacementCount > 0
      if (requireIntersection && gridHasOtherPlacements && evalResult.validIntersectionCount === 0) {
        continue
      }

      // Dedup key includes letters so distinct inflected forms of the same
      // (wordA, wordB) pair don't collide. pairingId being null is fine — the
      // letters keep the key unique.
      const pairKey = `${wordA.id}:${wordB.id}:${wordALetters}:${wordBLetters}:${startOffset}`
      if (addedPairs.has(pairKey)) continue
      addedPairs.add(pairKey)

      // Count valid positions for ranking (cache by literal pair text).
      const pairTextKey = `${wordAText}::${wordBText}`
      let conflictFreeCount = validPositionCountByText.get(pairTextKey)
      if (conflictFreeCount === undefined) {
        const validPositions = findValidPositions(grid, wordAText, wordBText)
        conflictFreeCount = validPositions.filter(p => p.conflicts.length === 0).length
        validPositionCountByText.set(pairTextKey, conflictFreeCount)
      }

      fittingPairs.push({
        pairingId,
        wordAId: wordA.id,
        wordBId: wordB.id,
        wordAText,
        wordBText,
        validPositionCount: conflictFreeCount,
        startOffset
      })
    }
  }

  // Sort by number of valid positions (fewer = more constrained = should place first)
  fittingPairs.sort((a, b) => a.validPositionCount - b.validPositionCount)

  return fittingPairs
}

// Find single words that fit at a given position with crossing constraints
// Returns words from either list that match the pattern for one side
export function findFittingSingleWords(
  grid: ConstructorGrid,
  row: number,
  col: number,
  direction: 'across' | 'down',
  maxLength: number,
  words: Word[],
  pairings: Pairing[],
  options: {
    requireIntersection?: boolean
    includeInflections?: boolean
    inflectionRules?: InflectionRuleOptions
    allowOutOfBounds?: boolean
    allowConflictsAwayFromActive?: boolean
    includePlaced?: boolean
  } = {}
): FittingSingleWord[] {
  const {
    requireIntersection = true,
    includeInflections = true,
    inflectionRules: rawInflectionRules,
    allowOutOfBounds = false,
    allowConflictsAwayFromActive = false,
    includePlaced = false,
  } = options
  const inflectionRules = { ...DEFAULT_INFLECTION_RULES, ...rawInflectionRules }
  const fittingSingleWords: FittingSingleWord[] = []
  const addedWords = new Set<string>() // Track unique word+offset combinations

  // Get words already used on grid
  const usedWordIds = getUsedWordIds(grid, pairings, words)

  // Get words with list assignment
  const assignedWords = words.filter(w => w.list)

  // For includePlaced: build cell ownership and a pairing→words index so
  // we can identify a candidate's own placements and mask their cells.
  const ownershipMaps = includePlaced ? buildCellOwnershipMaps(grid) : null
  const pairingsById = includePlaced ? new Map(pairings.map(p => [p.id, p])) : null

  // For each word, try all possible starting positions
  for (const word of assignedWords) {
    // Skip if word is already used
    if (!includePlaced && usedWordIds.has(word.id)) continue

    for (const form of getWordMatchForms(word, includeInflections, inflectionRules)) {
      const wordLetters = form.letters
      const wordLength = wordLetters.length

      if (wordLength > maxLength) continue

      // Identify placements that represent THIS candidate word: matched by
      // (a) pairing whose A or B word ID matches, or (b) cells whose letter
      // on the candidate's list side equals its letters. Track existing
      // positions so we can skip suggesting the candidate at its own spot.
      let ownCellKeys: Set<string> | undefined
      let ownPlacementCount = 0
      let ownPositionKeys: Set<string> | undefined
      if (ownershipMaps && pairingsById) {
        const ownIds = new Set<string>()
        for (const placement of grid.placements) {
          if (placement.pairingId) {
            const pairing = pairingsById.get(placement.pairingId)
            if (pairing && (pairing.wordAId === word.id || pairing.wordBId === word.id)) {
              ownIds.add(placement.id)
              continue
            }
          }
        }
        for (const id of ownershipMaps.placementsByWordOnList.get(`${word.list}:${wordLetters}`) ?? []) {
          ownIds.add(id)
        }
        if (ownIds.size > 0) {
          ownCellKeys = computeOwnCellKeys(ownIds, ownershipMaps)
          ownPlacementCount = ownIds.size
          ownPositionKeys = new Set()
          for (const p of grid.placements) {
            if (ownIds.has(p.id)) {
              ownPositionKeys.add(`${p.row},${p.col},${p.direction}`)
            }
          }
        }
      }

      // Try all possible starting positions where the word could pass through (row, col)
      for (let startOffset = 0; startOffset < wordLength; startOffset++) {
        const startRow = direction === 'down' ? row - startOffset : row
        const startCol = direction === 'across' ? col - startOffset : col

        // Skip if starting position is out of bounds
        if (startRow < 0 || startCol < 0) continue

        // Skip the candidate's own existing positions (would always re-match).
        if (ownPositionKeys?.has(`${startRow},${startCol},${direction}`)) continue

        // Single-word candidates only constrain the side that matches their
        // list assignment. Use '□' wildcards on the OTHER side so the shared
        // evaluator's per-side conflict / intersection logic still works —
        // wildcards never conflict and never count as match, which is what
        // we want for the unconstrained side.
        const wildcardSide = '□'.repeat(wordLength)
        const evalA = word.list === 'A' ? wordLetters : wildcardSide
        const evalB = word.list === 'A' ? wildcardSide : wordLetters
        const evalResult = evaluatePlacementCandidate(
          grid,
          startRow,
          startCol,
          direction,
          evalA,
          evalB,
          {
            ownCellKeys,
            allowOutOfBounds,
            allowConflictsAwayFromActive,
            activeCellIndex: startOffset,
          },
        )
        if (!evalResult.fits) continue

        // If grid is empty (or only contains this candidate's own placements),
        // allow placement without intersection. Otherwise require a *valid*
        // intersection (matching existing letter on the candidate's side).
        const gridHasOtherPlacements = grid.placements.length - ownPlacementCount > 0
        if (requireIntersection && gridHasOtherPlacements && evalResult.validIntersectionCount === 0) {
          continue
        }

        // Create unique key to avoid duplicate entries
        const wordKey = `${word.id}:${wordLetters}:${startOffset}`
        if (addedWords.has(wordKey)) continue
        addedWords.add(wordKey)

        fittingSingleWords.push({
          wordId: word.id,
          wordText: form.text,
          list: word.list as ListSide,
          startOffset
        })
      }
    }
  }

  // Sort alphabetically for now
  fittingSingleWords.sort((a, b) => a.wordText.localeCompare(b.wordText))

  return fittingSingleWords
}

// Get status priority for sorting (higher = better). Approved tops the list,
// draft below that, rejected last. Placement (used/unused) and quality
// (rating 1–5) are separate axes consumers can compose with this.
function getStatusPriority(status: PairingStatus | null): number {
  switch (status) {
    case 'approved': return 3
    case 'draft': return 2
    case 'none': return 1
    case 'rejected': return 0
    default: return -1  // null/virtual pairs
  }
}

export function findAutoSuggestions(
  grid: ConstructorGrid,
  words: Word[],
  pairings: Pairing[],
  maxResults: number = 50,
  options: { includeInflections?: boolean; inflectionRules?: InflectionRuleOptions; includePlaced?: boolean } = {}
): AutoSuggestion[] {
  const { includeInflections = true, inflectionRules, includePlaced = false } = options
  const suggestions: AutoSuggestion[] = []

  // Get words and pairings already used
  const usedPairingIds = getUsedPairingIds(grid)
  const usedWordIds = getUsedWordIds(grid, pairings, words)

  // Create a map for quick lookup of pairing status
  const pairingStatusMap = new Map<string, PairingStatus>()
  for (const p of pairings) {
    pairingStatusMap.set(p.id, p.status)
  }

  // Generate all valid pairs
  const maxWordLength = Math.max(grid.width, grid.height)
  const generatePairOptions: GeneratePairsOptions = {
    maxLength: maxWordLength,
    includeInflections,
    ...(inflectionRules ? { inflectionRules } : {})
  }
  const allPairs = generateAllValidPairs(words, pairings, generatePairOptions)

  // includePlaced: maps used to mask a candidate's own placements while
  // searching for other valid positions for it.
  const ownershipMaps = includePlaced ? buildCellOwnershipMaps(grid) : null

  // For each pair, find all valid positions with intersections
  for (const { pairingId, sourcePairingId, wordA, wordB, wordAText, wordBText, wordALetters, wordBLetters } of allPairs) {
    // Skip if already used
    if (!includePlaced && sourcePairingId && usedPairingIds.has(sourcePairingId)) continue
    if (!includePlaced && (usedWordIds.has(wordA.id) || usedWordIds.has(wordB.id))) continue

    const wordLength = wordALetters.length

    // Mask cells exclusively owned by this candidate's existing placements,
    // and track existing positions to skip when scanning.
    let ownCellKeys: Set<string> | undefined
    let ownPlacementCount = 0
    let ownPositionKeys: Set<string> | undefined
    if (ownershipMaps) {
      const ownIds = new Set<string>()
      if (sourcePairingId) {
        for (const id of ownershipMaps.placementsByPairingId.get(sourcePairingId) ?? []) {
          ownIds.add(id)
        }
      }
      for (const id of ownershipMaps.placementsByLetterCombo.get(`${wordALetters}::${wordBLetters}`) ?? []) {
        ownIds.add(id)
      }
      if (ownIds.size > 0) {
        ownCellKeys = computeOwnCellKeys(ownIds, ownershipMaps)
        ownPlacementCount = ownIds.size
        ownPositionKeys = new Set()
        for (const p of grid.placements) {
          if (ownIds.has(p.id)) {
            ownPositionKeys.add(`${p.row},${p.col},${p.direction}`)
          }
        }
      }
    }

    // Try every position and direction. evaluatePlacementCandidate handles
    // bounds, blocked cells, conflicts (with '□' wildcards + ownCellKeys
    // masking), and adjacency in one pass.
    for (let row = 0; row < grid.height; row++) {
      for (let col = 0; col < grid.width; col++) {
        for (const direction of ['across', 'down'] as const) {
          if (ownPositionKeys?.has(`${row},${col},${direction}`)) continue

          const evalResult = evaluatePlacementCandidate(
            grid,
            row,
            col,
            direction,
            wordALetters,
            wordBLetters,
            { ownCellKeys },
          )
          if (!evalResult.fits) continue

          // Count intersections via the existing helper — its ownCellKeys
          // masking matches the evaluator's so the count reflects what we
          // actually want (intersections against OTHER placements).
          const intersectionCount = countIntersections(grid, row, col, wordLength, direction, ownCellKeys)

          // Only include if it creates at least one intersection (unless grid is empty
          // or only contains this candidate's own placements)
          const gridHasOtherPlacements = grid.placements.length - ownPlacementCount > 0
          if (gridHasOtherPlacements && intersectionCount === 0) continue

          const pairingStatus = sourcePairingId ? pairingStatusMap.get(sourcePairingId) ?? null : null

          suggestions.push({
            pairingId: pairingId ?? sourcePairingId,
            wordAId: wordA.id,
            wordBId: wordB.id,
            wordAText,
            wordBText,
            row,
            col,
            direction,
            intersectionCount,
            pairingStatus
          })
        }
      }
    }
  }

  // Sort by status first (favourite/approved first), then by word length (shorter first)
  suggestions.sort((a, b) => {
    const statusDiff = getStatusPriority(b.pairingStatus) - getStatusPriority(a.pairingStatus)
    if (statusDiff !== 0) return statusDiff
    const lenA = a.wordAText.replace(/\s/g, '').length
    const lenB = b.wordAText.replace(/\s/g, '').length
    return lenA - lenB
  })

  return suggestions.slice(0, maxResults)
}

// Toggle a cell's blocked state
export function toggleCellBlocked(
  grid: ConstructorGrid,
  row: number,
  col: number
): ConstructorGrid {
  if (row < 0 || row >= grid.height || col < 0 || col >= grid.width) {
    return grid
  }

  const cell = grid.cells[row]?.[col]
  if (!cell) return grid

  // Don't allow blocking cells that have placements
  if (cell.placementId !== null) {
    return grid
  }

  const newGrid: ConstructorGrid = {
    ...grid,
    cells: grid.cells.map((r, ri) =>
      r.map((c, ci) =>
        ri === row && ci === col
          ? { ...c, isBlocked: !c.isBlocked }
          : { ...c }
      )
    )
  }

  return newGrid
}

// Toggle multiple cells' blocked state in one atomic operation.
// Cells with placements are ignored.
export function toggleCellsBlocked(
  grid: ConstructorGrid,
  cells: Array<{ row: number; col: number }>
): ConstructorGrid {
  if (cells.length === 0) return grid

  const uniqueCells = new Map<string, { row: number; col: number }>()
  for (const cell of cells) {
    const key = `${cell.row},${cell.col}`
    if (!uniqueCells.has(key)) uniqueCells.set(key, cell)
  }

  const togglable = new Set<string>()
  for (const { row, col } of uniqueCells.values()) {
    if (row < 0 || row >= grid.height || col < 0 || col >= grid.width) continue
    const cell = grid.cells[row]?.[col]
    if (!cell) continue
    if (cell.placementId !== null) continue
    togglable.add(`${row},${col}`)
  }

  if (togglable.size === 0) return grid

  return {
    ...grid,
    cells: grid.cells.map((rowCells, row) =>
      rowCells.map((cell, col) =>
        togglable.has(`${row},${col}`)
          ? { ...cell, isBlocked: !cell.isBlocked }
          : { ...cell }
      )
    )
  }
}

// Resize the grid (preserves placements that still fit)
// rowOffset/colOffset shift all placements when resizing from corners other than SE
export function resizeGrid(
  grid: ConstructorGrid,
  newWidth: number,
  newHeight: number,
  rowOffset: number = 0,
  colOffset: number = 0
): ConstructorGrid {
  // Create new empty grid
  const newGrid = createGrid(newWidth, newHeight)

  // Keep ALL placements (even those outside bounds)
  // Apply offset to shift placements when grid origin changes
  for (const placement of grid.placements) {
    const wordALetters = getLetterArray(placement.wordAText)
    const wordBLetters = getLetterArray(placement.wordBText)
    const wordLength = wordALetters.length

    // Add the placement with adjusted position
    const adjustedPlacement = {
      ...placement,
      row: placement.row + rowOffset,
      col: placement.col + colOffset
    }
    newGrid.placements.push(adjustedPlacement)

    // Update cell data only for cells within bounds
    for (let i = 0; i < wordLength; i++) {
      const row = adjustedPlacement.direction === 'down' ? adjustedPlacement.row + i : adjustedPlacement.row
      const col = adjustedPlacement.direction === 'across' ? adjustedPlacement.col + i : adjustedPlacement.col
      const letterA = wordALetters[i]
      const letterB = wordBLetters[i]

      // Only update cells that are within the new grid bounds
      if (row >= 0 && row < newHeight && col >= 0 && col < newWidth) {
        const cell = newGrid.cells[row]?.[col]
        if (!cell || letterA === undefined || letterB === undefined) continue
        cell.letterA = letterA
        cell.letterB = letterB
        cell.placementId = adjustedPlacement.id
      }
    }
  }

  // Preserve blocked cells, applying the same origin offset as placements.
  for (let row = 0; row < grid.height; row++) {
    for (let col = 0; col < grid.width; col++) {
      const oldCell = grid.cells[row]?.[col]
      const newRow = row + rowOffset
      const newCol = col + colOffset
      if (newRow < 0 || newRow >= newHeight || newCol < 0 || newCol >= newWidth) continue

      const newCell = newGrid.cells[newRow]?.[newCol]
      if (oldCell?.isBlocked && newCell && !newCell.placementId) {
        newCell.isBlocked = true
      }
    }
  }

  return newGrid
}

// Clear all placements from the grid
export function clearGrid(grid: ConstructorGrid): ConstructorGrid {
  return createGrid(grid.width, grid.height)
}

// Move a placement to a new position
export function movePlacement(
  grid: ConstructorGrid,
  placementId: string,
  newRow: number,
  newCol: number
): ConstructorGrid {
  const placement = grid.placements.find(p => p.id === placementId)
  if (!placement) return grid

  // Remove the old placement
  const gridWithoutPlacement = removePlacement(grid, placementId)

  // Try to place at new position
  const result = placePair(gridWithoutPlacement, {
    ...placement,
    row: newRow,
    col: newCol
  })

  // If placement fails, return original grid
  return result.success ? result.grid : grid
}

// Move multiple placements in one atomic update so undo/redo treats it as a single step.
export function movePlacements(
  grid: ConstructorGrid,
  updates: Array<{ placementId: string; newRow: number; newCol: number }>
): ConstructorGrid {
  const targets = updates
    .map(u => {
      const placement = grid.placements.find(p => p.id === u.placementId)
      if (!placement) return null
      return { placement, newRow: u.newRow, newCol: u.newCol }
    })
    .filter((t): t is { placement: GridPlacement; newRow: number; newCol: number } => t !== null)

  if (targets.length === 0) return grid

  let nextGrid = grid
  for (const { placement } of targets) {
    nextGrid = removePlacement(nextGrid, placement.id)
  }
  for (const { placement, newRow, newCol } of targets) {
    const result = placePair(nextGrid, {
      ...placement,
      row: newRow,
      col: newCol
    })
    if (result.success) {
      nextGrid = result.grid
    } else {
      return grid
    }
  }
  return nextGrid
}

export function removePlacements(
  grid: ConstructorGrid,
  placementIds: readonly string[]
): ConstructorGrid {
  let nextGrid = grid
  for (const id of placementIds) {
    nextGrid = removePlacement(nextGrid, id)
  }
  return nextGrid
}

// Rotate a placement (toggle between across and down)
export function rotatePlacement(
  grid: ConstructorGrid,
  placementId: string
): ConstructorGrid {
  const placement = grid.placements.find(p => p.id === placementId)
  if (!placement) return grid

  const newDirection = placement.direction === 'across' ? 'down' : 'across'

  // Remove the old placement
  const gridWithoutPlacement = removePlacement(grid, placementId)

  // Try to place with new direction
  const result = placePair(gridWithoutPlacement, {
    ...placement,
    direction: newDirection
  })

  // If rotation fails, return original grid
  return result.success ? result.grid : grid
}

// Build a grid whose cells array spans every existing placement, plus padding.
// Scratchpad placements live in a 1×1 internal grid with arbitrary world coords;
// suggestion search functions iterate `0..width × 0..height`, so they can't see
// placements outside that 1×1 area. This helper produces an equivalent grid
// where placements are shifted into non-negative coords inside a properly-sized
// cells array, and returns the offset so callers can map back to world coords.
// Update a single letter in a placement at a given index
// Used for filling in placeholder (□) characters
export function updatePlacementLetter(
  grid: ConstructorGrid,
  placementId: string,
  letterIndex: number,
  newLetter: string,
  side: 'A' | 'B'
): ConstructorGrid {
  const placement = grid.placements.find(p => p.id === placementId)
  if (!placement) return grid

  const wordText = side === 'A' ? placement.wordAText : placement.wordBText
  const letters = wordText.replace(/\s/g, '').split('')

  if (letterIndex < 0 || letterIndex >= letters.length) return grid

  // Update the letter
  letters[letterIndex] = newLetter.toUpperCase()
  const newWordText = letters.join('')

  // Create updated placement
  const updatedPlacement: GridPlacement = {
    ...placement,
    wordAText: side === 'A' ? newWordText : placement.wordAText,
    wordBText: side === 'B' ? newWordText : placement.wordBText
  }

  // Clone the grid with updated placement
  const newGrid: ConstructorGrid = {
    ...grid,
    cells: grid.cells.map(row => row.map(cell => ({ ...cell }))),
    placements: grid.placements.map(p => p.id === placementId ? updatedPlacement : p)
  }

  // Update the cell letter
  const row = placement.direction === 'down' ? placement.row + letterIndex : placement.row
  const col = placement.direction === 'across' ? placement.col + letterIndex : placement.col

  if (row >= 0 && row < grid.height && col >= 0 && col < grid.width) {
    const cell = newGrid.cells[row]?.[col]
    if (cell) {
      if (side === 'A') {
        cell.letterA = newLetter.toUpperCase()
      } else {
        cell.letterB = newLetter.toUpperCase()
      }
    }
  }

  return newGrid
}
